import { PropsWithChildren } from 'react';
import { makeStyles, styled } from '@material-ui/core';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  useSidebarOpenState,
  Link
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import GroupIcon from '@material-ui/icons/People';
import FaceRounded from '@material-ui/icons/FaceRounded';
import GpsFixedIcon from '@material-ui/icons/GpsFixed';
import LayersIcon from '@material-ui/icons/Layers';
import ExploreIcon from '@material-ui/icons/Explore';
import CategoryIcon from '@material-ui/icons/Category';
import BuildIcon from '@material-ui/icons/Build';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { devToolsAdministerPermission } from '@backstage/plugin-devtools-common';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

const SidebarDivider = styled('hr')(({ theme }) => ({
  height: 1,
  width: '100%',
  background: 'rgba(114, 116, 117, 0.32)',
  border: 'none',
  margin: theme.spacing(1.2, 0),
}));

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        <SidebarItem icon={FaceRounded} to='/my-space' text='My Space' data-testid="my-space-link" />
        <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" data-testid="catalog-link" />
        <MyGroupsSidebarItem
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={GroupIcon}
        />
        <SidebarItem icon={LayersIcon} to="api-docs" text="APIs" data-testid="api-docs-link" />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" data-testid="docs-link" />
        <SidebarItem icon={ExploreIcon} to="explore" text="Explore" data-testid="explore-link" />
        <SidebarItem icon={VerifiedUserIcon} to="entity-validation" text="Validator" data-testid="validator-link" />
        <RequirePermission permission={catalogEntityCreatePermission} errorPage={<></>}>
          <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." data-testid="create-link" />
        </RequirePermission>
      </SidebarGroup>
      <SidebarDivider />
      <SidebarItem icon={GpsFixedIcon} to="tech-radar" text="Tech Radar" data-testid="tech-radar-link" />
      <SidebarSpace />
      <SidebarDivider />
      <NotificationsSidebarItem />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings">
        <SidebarSettings />
      </SidebarGroup>
      <RequirePermission permission={devToolsAdministerPermission} errorPage={<></>}>
        <SidebarItem icon={BuildIcon} to="devtools" text="DevTools" data-testid="devtools-link" />
      </RequirePermission>
    </Sidebar>
    {children}
  </SidebarPage>
);
